import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  X,
  Search,
  FileCode,
  Smartphone,
  Globe,
  Layout,
  ShoppingCart,
  Users,
  Briefcase,
  Lightbulb,
  CheckCircle2,
  Clock,
  Target,
  ListTodo,
  FolderKanban,
  BookOpen,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { LucideIcon } from 'lucide-react';

import { projectTemplatesService, type ProjectTemplateFromApi } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

// Template category type
type TemplateCategory = 'all' | 'software' | 'marketing' | 'business' | 'research';

const ICON_MAP: Record<string, LucideIcon> = {
  FileCode,
  Smartphone,
  Globe,
  Layout,
  ShoppingCart,
  Users,
  Briefcase,
  Lightbulb,
  FolderKanban,
};
function apiToModal(api: ProjectTemplateFromApi): ProjectTemplate {
  return {
    ...api,
    icon: ICON_MAP[api.icon] ?? FolderKanban,
    phases: api.phases.map((p) => ({ ...p, tasks: p.tasks ?? [] })),
  };
}

// Project template interface
export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  icon: LucideIcon;
  color: string;
  estimatedDuration: string;
  taskCount: number;
  milestoneCount: number;
  tags: string[];
  phases: {
    name: string;
    tasks: string[];
    duration: string;
  }[];
  defaultTasks: {
    name: string;
    description?: string;
    estimatedMinutes: number;
    phase: string;
  }[];
  defaultMilestones: {
    name: string;
    description?: string;
    phaseIndex: number;
  }[];
}

// Predefined project templates
export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'software-dev',
    name: 'Software Development',
    description: 'Full software development lifecycle from planning to deployment',
    category: 'software',
    icon: FileCode,
    color: '#2563eb',
    estimatedDuration: '3-6 months',
    taskCount: 24,
    milestoneCount: 6,
    tags: ['agile', 'development', 'deployment'],
    phases: [
      { name: 'Planning', tasks: ['Requirements gathering', 'Technical design', 'Sprint planning'], duration: '2 weeks' },
      { name: 'Development', tasks: ['Backend development', 'Frontend development', 'API integration'], duration: '8 weeks' },
      { name: 'Testing', tasks: ['Unit testing', 'Integration testing', 'UAT'], duration: '2 weeks' },
      { name: 'Deployment', tasks: ['Staging deployment', 'Production deployment', 'Monitoring setup'], duration: '1 week' },
    ],
    defaultTasks: [
      { name: 'Gather requirements', estimatedMinutes: 16, phase: 'Planning' },
      { name: 'Create technical specifications', estimatedMinutes: 24, phase: 'Planning' },
      { name: 'Set up development environment', estimatedMinutes: 8, phase: 'Planning' },
      { name: 'Implement core features', estimatedMinutes: 80, phase: 'Development' },
      { name: 'Build user interface', estimatedMinutes: 60, phase: 'Development' },
      { name: 'API development', estimatedMinutes: 40, phase: 'Development' },
      { name: 'Write unit tests', estimatedMinutes: 24, phase: 'Testing' },
      { name: 'Perform integration testing', estimatedMinutes: 16, phase: 'Testing' },
      { name: 'User acceptance testing', estimatedMinutes: 16, phase: 'Testing' },
      { name: 'Deploy to staging', estimatedMinutes: 8, phase: 'Deployment' },
      { name: 'Deploy to production', estimatedMinutes: 4, phase: 'Deployment' },
      { name: 'Post-deployment monitoring', estimatedMinutes: 8, phase: 'Deployment' },
    ],
    defaultMilestones: [
      { name: 'Requirements Approved', phaseIndex: 0 },
      { name: 'Development Complete', phaseIndex: 1 },
      { name: 'Testing Complete', phaseIndex: 2 },
      { name: 'Go Live', phaseIndex: 3 },
    ],
  },
  {
    id: 'mobile-app',
    name: 'Mobile App Development',
    description: 'iOS and Android app development with cross-platform considerations',
    category: 'software',
    icon: Smartphone,
    color: '#16a34a',
    estimatedDuration: '4-6 months',
    taskCount: 28,
    milestoneCount: 5,
    tags: ['mobile', 'ios', 'android', 'react-native'],
    phases: [
      { name: 'Discovery', tasks: ['Market research', 'User personas', 'Feature prioritization'], duration: '2 weeks' },
      { name: 'Design', tasks: ['Wireframes', 'UI design', 'Prototype'], duration: '3 weeks' },
      { name: 'Development', tasks: ['Core features', 'API integration', 'Push notifications'], duration: '10 weeks' },
      { name: 'Testing & Launch', tasks: ['Beta testing', 'App store submission', 'Marketing'], duration: '3 weeks' },
    ],
    defaultTasks: [
      { name: 'Conduct market research', estimatedMinutes: 20, phase: 'Discovery' },
      { name: 'Define user personas', estimatedMinutes: 12, phase: 'Discovery' },
      { name: 'Create wireframes', estimatedMinutes: 24, phase: 'Design' },
      { name: 'Design UI/UX', estimatedMinutes: 40, phase: 'Design' },
      { name: 'Build prototype', estimatedMinutes: 16, phase: 'Design' },
      { name: 'Develop core features', estimatedMinutes: 120, phase: 'Development' },
      { name: 'Integrate APIs', estimatedMinutes: 40, phase: 'Development' },
      { name: 'Implement push notifications', estimatedMinutes: 16, phase: 'Development' },
      { name: 'Conduct beta testing', estimatedMinutes: 24, phase: 'Testing & Launch' },
      { name: 'Submit to app stores', estimatedMinutes: 8, phase: 'Testing & Launch' },
    ],
    defaultMilestones: [
      { name: 'Discovery Complete', phaseIndex: 0 },
      { name: 'Design Approved', phaseIndex: 1 },
      { name: 'Beta Release', phaseIndex: 2 },
      { name: 'App Store Launch', phaseIndex: 3 },
    ],
  },
  {
    id: 'website-redesign',
    name: 'Website Redesign',
    description: 'Complete website overhaul with modern UX and performance optimization',
    category: 'software',
    icon: Globe,
    color: '#9333ea',
    estimatedDuration: '2-3 months',
    taskCount: 18,
    milestoneCount: 4,
    tags: ['web', 'redesign', 'ux', 'seo'],
    phases: [
      { name: 'Audit', tasks: ['Current site analysis', 'Competitor research', 'SEO audit'], duration: '1 week' },
      { name: 'Design', tasks: ['Mood boards', 'Page layouts', 'Component library'], duration: '3 weeks' },
      { name: 'Development', tasks: ['Frontend build', 'CMS integration', 'Performance optimization'], duration: '4 weeks' },
      { name: 'Launch', tasks: ['Content migration', 'Testing', 'Go live'], duration: '1 week' },
    ],
    defaultTasks: [
      { name: 'Analyze current site', estimatedMinutes: 8, phase: 'Audit' },
      { name: 'Research competitors', estimatedMinutes: 12, phase: 'Audit' },
      { name: 'Conduct SEO audit', estimatedMinutes: 8, phase: 'Audit' },
      { name: 'Create mood boards', estimatedMinutes: 8, phase: 'Design' },
      { name: 'Design page layouts', estimatedMinutes: 32, phase: 'Design' },
      { name: 'Build component library', estimatedMinutes: 24, phase: 'Design' },
      { name: 'Develop frontend', estimatedMinutes: 60, phase: 'Development' },
      { name: 'Integrate CMS', estimatedMinutes: 20, phase: 'Development' },
      { name: 'Optimize performance', estimatedMinutes: 16, phase: 'Development' },
      { name: 'Migrate content', estimatedMinutes: 16, phase: 'Launch' },
      { name: 'Final testing', estimatedMinutes: 8, phase: 'Launch' },
    ],
    defaultMilestones: [
      { name: 'Audit Complete', phaseIndex: 0 },
      { name: 'Design Approved', phaseIndex: 1 },
      { name: 'Development Complete', phaseIndex: 2 },
      { name: 'Site Launch', phaseIndex: 3 },
    ],
  },
  {
    id: 'marketing-campaign',
    name: 'Marketing Campaign',
    description: 'End-to-end marketing campaign from strategy to execution and analysis',
    category: 'marketing',
    icon: Layout,
    color: '#f59e0b',
    estimatedDuration: '1-2 months',
    taskCount: 16,
    milestoneCount: 4,
    tags: ['marketing', 'campaign', 'digital', 'content'],
    phases: [
      { name: 'Strategy', tasks: ['Goal setting', 'Audience research', 'Channel selection'], duration: '1 week' },
      { name: 'Content Creation', tasks: ['Copywriting', 'Design assets', 'Video production'], duration: '2 weeks' },
      { name: 'Execution', tasks: ['Campaign launch', 'Ad management', 'Social posting'], duration: '4 weeks' },
      { name: 'Analysis', tasks: ['Performance review', 'ROI calculation', 'Optimization'], duration: '1 week' },
    ],
    defaultTasks: [
      { name: 'Define campaign goals', estimatedMinutes: 4, phase: 'Strategy' },
      { name: 'Research target audience', estimatedMinutes: 8, phase: 'Strategy' },
      { name: 'Select marketing channels', estimatedMinutes: 4, phase: 'Strategy' },
      { name: 'Write campaign copy', estimatedMinutes: 16, phase: 'Content Creation' },
      { name: 'Create design assets', estimatedMinutes: 24, phase: 'Content Creation' },
      { name: 'Launch campaign', estimatedMinutes: 4, phase: 'Execution' },
      { name: 'Manage paid ads', estimatedMinutes: 20, phase: 'Execution' },
      { name: 'Monitor social engagement', estimatedMinutes: 16, phase: 'Execution' },
      { name: 'Analyze performance', estimatedMinutes: 8, phase: 'Analysis' },
      { name: 'Calculate ROI', estimatedMinutes: 4, phase: 'Analysis' },
      { name: 'Prepare final report', estimatedMinutes: 8, phase: 'Analysis' },
    ],
    defaultMilestones: [
      { name: 'Strategy Approved', phaseIndex: 0 },
      { name: 'Content Ready', phaseIndex: 1 },
      { name: 'Campaign Launched', phaseIndex: 2 },
      { name: 'Campaign Complete', phaseIndex: 3 },
    ],
  },
  {
    id: 'product-launch',
    name: 'Product Launch',
    description: 'Comprehensive product launch planning and execution',
    category: 'business',
    icon: ShoppingCart,
    color: '#dc2626',
    estimatedDuration: '2-3 months',
    taskCount: 22,
    milestoneCount: 5,
    tags: ['product', 'launch', 'go-to-market', 'sales'],
    phases: [
      { name: 'Preparation', tasks: ['Market analysis', 'Pricing strategy', 'Sales enablement'], duration: '3 weeks' },
      { name: 'Pre-Launch', tasks: ['Beta program', 'Press outreach', 'Partner alignment'], duration: '2 weeks' },
      { name: 'Launch', tasks: ['Launch event', 'PR push', 'Sales activation'], duration: '1 week' },
      { name: 'Post-Launch', tasks: ['Customer feedback', 'Performance tracking', 'Iteration'], duration: '4 weeks' },
    ],
    defaultTasks: [
      { name: 'Analyze target market', estimatedMinutes: 16, phase: 'Preparation' },
      { name: 'Define pricing strategy', estimatedMinutes: 12, phase: 'Preparation' },
      { name: 'Create sales materials', estimatedMinutes: 24, phase: 'Preparation' },
      { name: 'Run beta program', estimatedMinutes: 40, phase: 'Pre-Launch' },
      { name: 'Coordinate press outreach', estimatedMinutes: 16, phase: 'Pre-Launch' },
      { name: 'Execute launch event', estimatedMinutes: 24, phase: 'Launch' },
      { name: 'Coordinate PR campaign', estimatedMinutes: 16, phase: 'Launch' },
      { name: 'Activate sales team', estimatedMinutes: 8, phase: 'Launch' },
      { name: 'Collect customer feedback', estimatedMinutes: 20, phase: 'Post-Launch' },
      { name: 'Track launch metrics', estimatedMinutes: 16, phase: 'Post-Launch' },
    ],
    defaultMilestones: [
      { name: 'Go-to-Market Plan Ready', phaseIndex: 0 },
      { name: 'Beta Complete', phaseIndex: 1 },
      { name: 'Launch Day', phaseIndex: 2 },
      { name: 'Post-Launch Review', phaseIndex: 3 },
    ],
  },
  {
    id: 'team-onboarding',
    name: 'Team Onboarding',
    description: 'Structured onboarding program for new team members',
    category: 'business',
    icon: Users,
    color: '#0891b2',
    estimatedDuration: '2-4 weeks',
    taskCount: 14,
    milestoneCount: 3,
    tags: ['hr', 'onboarding', 'training', 'culture'],
    phases: [
      { name: 'Pre-boarding', tasks: ['Document preparation', 'Equipment setup', 'Welcome package'], duration: '3 days' },
      { name: 'Week 1', tasks: ['Orientation', 'Team introductions', 'Tool access'], duration: '1 week' },
      { name: 'Weeks 2-4', tasks: ['Role training', 'Shadowing', 'First project'], duration: '3 weeks' },
    ],
    defaultTasks: [
      { name: 'Prepare offer documents', estimatedMinutes: 2, phase: 'Pre-boarding' },
      { name: 'Set up workstation', estimatedMinutes: 4, phase: 'Pre-boarding' },
      { name: 'Send welcome package', estimatedMinutes: 2, phase: 'Pre-boarding' },
      { name: 'Conduct orientation', estimatedMinutes: 4, phase: 'Week 1' },
      { name: 'Introduce to team', estimatedMinutes: 4, phase: 'Week 1' },
      { name: 'Set up tool access', estimatedMinutes: 4, phase: 'Week 1' },
      { name: 'Role-specific training', estimatedMinutes: 20, phase: 'Weeks 2-4' },
      { name: 'Shadow experienced team member', estimatedMinutes: 16, phase: 'Weeks 2-4' },
      { name: 'Complete first project', estimatedMinutes: 24, phase: 'Weeks 2-4' },
    ],
    defaultMilestones: [
      { name: 'First Day Complete', phaseIndex: 1 },
      { name: 'Training Complete', phaseIndex: 2 },
      { name: 'Fully Onboarded', phaseIndex: 2 },
    ],
  },
  {
    id: 'research-project',
    name: 'Research Project',
    description: 'Structured research project with data collection and analysis',
    category: 'research',
    icon: Lightbulb,
    color: '#8b5cf6',
    estimatedDuration: '2-4 months',
    taskCount: 16,
    milestoneCount: 4,
    tags: ['research', 'data', 'analysis', 'insights'],
    phases: [
      { name: 'Planning', tasks: ['Define objectives', 'Literature review', 'Methodology'], duration: '2 weeks' },
      { name: 'Data Collection', tasks: ['Survey design', 'Interviews', 'Data gathering'], duration: '4 weeks' },
      { name: 'Analysis', tasks: ['Data processing', 'Statistical analysis', 'Pattern identification'], duration: '3 weeks' },
      { name: 'Reporting', tasks: ['Write findings', 'Create presentations', 'Peer review'], duration: '2 weeks' },
    ],
    defaultTasks: [
      { name: 'Define research objectives', estimatedMinutes: 8, phase: 'Planning' },
      { name: 'Conduct literature review', estimatedMinutes: 24, phase: 'Planning' },
      { name: 'Design methodology', estimatedMinutes: 16, phase: 'Planning' },
      { name: 'Create survey instruments', estimatedMinutes: 16, phase: 'Data Collection' },
      { name: 'Conduct interviews', estimatedMinutes: 40, phase: 'Data Collection' },
      { name: 'Process data', estimatedMinutes: 24, phase: 'Analysis' },
      { name: 'Perform statistical analysis', estimatedMinutes: 32, phase: 'Analysis' },
      { name: 'Write research report', estimatedMinutes: 32, phase: 'Reporting' },
      { name: 'Create presentation', estimatedMinutes: 12, phase: 'Reporting' },
      { name: 'Peer review and revisions', estimatedMinutes: 16, phase: 'Reporting' },
    ],
    defaultMilestones: [
      { name: 'Research Plan Approved', phaseIndex: 0 },
      { name: 'Data Collection Complete', phaseIndex: 1 },
      { name: 'Analysis Complete', phaseIndex: 2 },
      { name: 'Final Report Delivered', phaseIndex: 3 },
    ],
  },
  {
    id: 'event-planning',
    name: 'Event Planning',
    description: 'Complete event planning from concept to post-event follow-up',
    category: 'business',
    icon: Briefcase,
    color: '#ea580c',
    estimatedDuration: '1-3 months',
    taskCount: 20,
    milestoneCount: 4,
    tags: ['event', 'planning', 'logistics', 'coordination'],
    phases: [
      { name: 'Concept', tasks: ['Define objectives', 'Budget planning', 'Date selection'], duration: '1 week' },
      { name: 'Planning', tasks: ['Venue booking', 'Vendor coordination', 'Marketing'], duration: '4 weeks' },
      { name: 'Execution', tasks: ['Setup', 'Event day', 'Breakdown'], duration: '3 days' },
      { name: 'Post-Event', tasks: ['Thank yous', 'Feedback collection', 'ROI analysis'], duration: '1 week' },
    ],
    defaultTasks: [
      { name: 'Define event objectives', estimatedMinutes: 4, phase: 'Concept' },
      { name: 'Create event budget', estimatedMinutes: 8, phase: 'Concept' },
      { name: 'Book venue', estimatedMinutes: 12, phase: 'Planning' },
      { name: 'Coordinate vendors', estimatedMinutes: 24, phase: 'Planning' },
      { name: 'Create marketing materials', estimatedMinutes: 16, phase: 'Planning' },
      { name: 'Manage registrations', estimatedMinutes: 12, phase: 'Planning' },
      { name: 'Event setup', estimatedMinutes: 8, phase: 'Execution' },
      { name: 'Run event', estimatedMinutes: 12, phase: 'Execution' },
      { name: 'Event breakdown', estimatedMinutes: 4, phase: 'Execution' },
      { name: 'Send thank you notes', estimatedMinutes: 4, phase: 'Post-Event' },
      { name: 'Collect feedback', estimatedMinutes: 8, phase: 'Post-Event' },
      { name: 'Analyze event ROI', estimatedMinutes: 8, phase: 'Post-Event' },
    ],
    defaultMilestones: [
      { name: 'Event Concept Approved', phaseIndex: 0 },
      { name: 'Venue Booked', phaseIndex: 1 },
      { name: 'Event Day', phaseIndex: 2 },
      { name: 'Post-Event Review Complete', phaseIndex: 3 },
    ],
  },
];

interface ProjectTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: ProjectTemplate) => void;
}

/**
 * ProjectTemplateModal
 * Modal for selecting project templates to create new projects
 */
const ProjectTemplateModal: React.FC<ProjectTemplateModalProps> = ({
  open,
  onClose,
  onSelectTemplate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);

  const { data: apiTemplates = [] } = useQuery({
    queryKey: [...QUERY_KEYS.projects, 'templates'],
    queryFn: () => projectTemplatesService.getList(),
    enabled: open,
  });
  const apiTemplatesMapped = useMemo(
    () => apiTemplates.map(apiToModal),
    [apiTemplates]
  );
  const allTemplates = useMemo(
    () => [...apiTemplatesMapped, ...PROJECT_TEMPLATES],
    [apiTemplatesMapped]
  );

  // Filter templates
  const filteredTemplates = allTemplates.filter((template) => {
    const matchesSearch =
      !searchQuery ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory =
      selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSelectTemplate = async (template: ProjectTemplate) => {
    const fromApi = apiTemplatesMapped.some((t) => t.id === template.id);
    if (fromApi && template.taskCount === 0) {
      const full = await projectTemplatesService.getById(template.id);
      if (full) setSelectedTemplate(apiToModal(full));
      else setSelectedTemplate(template);
    } else {
      setSelectedTemplate(template);
    }
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSearchQuery('');
    setSelectedCategory('all');
    onClose();
  };

  const categories: { value: TemplateCategory; label: string }[] = [
    { value: 'all', label: 'All Templates' },
    { value: 'software', label: 'Software' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'business', label: 'Business' },
    { value: 'research', label: 'Research' },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh', height: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderKanban size={24} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Project Templates
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Start with a pre-built template to save time
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ display: 'flex', p: 0, overflow: 'hidden' }}>
        {/* Template List */}
        <Box sx={{ width: selectedTemplate ? '55%' : '100%', p: 2, overflow: 'auto' }}>
          {/* Search and Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Category Chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <Chip
                key={cat.value}
                label={cat.label}
                onClick={() => setSelectedCategory(cat.value)}
                color={selectedCategory === cat.value ? 'primary' : 'default'}
                variant={selectedCategory === cat.value ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          {/* Template Grid */}
          <Grid container spacing={2}>
            {filteredTemplates.map((template) => {
              const TemplateIcon = template.icon;
              const isSelected = selectedTemplate?.id === template.id;

              return (
                <Grid item xs={12} sm={selectedTemplate ? 12 : 6} md={selectedTemplate ? 6 : 4} key={template.id}>
                  <Card
                    onClick={() => handleSelectTemplate(template)}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'primary.light',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: `${template.color}15`,
                            width: 40,
                            height: 40,
                          }}
                        >
                          <TemplateIcon size={20} style={{ color: template.color }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {template.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {template.description}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <CheckCircle2 size={20} style={{ color: '#2563eb' }} />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Clock size={12} />
                          <Typography variant="caption">{template.estimatedDuration}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ListTodo size={12} />
                          <Typography variant="caption">{template.taskCount} tasks</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Target size={12} />
                          <Typography variant="caption">{template.milestoneCount} milestones</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {template.tags.slice(0, 3).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: `${template.color}15`,
                              color: template.color,
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {filteredTemplates.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <BookOpen size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
              <Typography variant="body1" color="text.secondary">
                No templates found matching your search
              </Typography>
            </Box>
          )}
        </Box>

        {/* Template Details Panel */}
        {selectedTemplate && (
          <Box
            sx={{
              width: '45%',
              borderLeft: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              p: 3,
              overflow: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: `${selectedTemplate.color}20`,
                  width: 48,
                  height: 48,
                }}
              >
                {React.createElement(selectedTemplate.icon, {
                  size: 24,
                  style: { color: selectedTemplate.color },
                })}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {selectedTemplate.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedTemplate.description}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedTemplate.estimatedDuration}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tasks
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedTemplate.taskCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Milestones
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedTemplate.milestoneCount}
                </Typography>
              </Box>
            </Box>

            {/* Phases */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Project Phases
            </Typography>
            <List dense disablePadding sx={{ mb: 2 }}>
              {selectedTemplate.phases.map((phase, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: `${selectedTemplate.color}20`,
                        color: selectedTemplate.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={phase.name}
                    secondary={`${phase.duration} • ${phase.tasks.length} tasks`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Milestones */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Key Milestones
            </Typography>
            <List dense disablePadding>
              {selectedTemplate.defaultMilestones.map((milestone, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Target size={14} style={{ color: '#9333ea' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={milestone.name}
                    secondary={`After ${selectedTemplate.phases[milestone.phaseIndex]?.name}`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedTemplate}
          onClick={handleConfirm}
        >
          Use Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectTemplateModal;
